// Configurazione unica delle chiamate al server.
//
// I componenti continuano a usare axios direttamente: gli interceptor qui sotto
// agiscono sull'istanza condivisa, quindi valgono per tutte le chiamate senza
// doverle riscrivere una per una.
//
// Importato da index.js prima che l'app parta.

import axios from 'axios';
import { Preferences } from '@capacitor/preferences';
import { apiUrl } from './components/config';

let onUnauthorized = null;

// App.js registra qui la navigazione, così il rientro al login non ricarica
// tutta l'applicazione. Senza handler si ripiega su un cambio di indirizzo.
export const setUnauthorizedHandler = (handler) => {
    onUnauthorized = handler;
};

// Solo le chiamate al nostro server ricevono il token: mandarlo a servizi
// esterni (per esempio la verifica versione su App Store) lo esporrebbe.
const isOurApi = (url) => {
    if (!url) return false;
    return url.startsWith(apiUrl) || url.startsWith('/api');
};

axios.interceptors.request.use(async (config) => {
    if (!isOurApi(config.url)) return config;

    config.headers = config.headers || {};

    // Se una chiamata passa già il proprio header, viene rispettato.
    if (config.headers.Authorization) return config;

    try {
        const { value } = await Preferences.get({ key: 'token' });
        if (value) {
            config.headers.Authorization = `Bearer ${value}`;
        }
    } catch (error) {
        // Nessun token disponibile: la richiesta parte comunque e sarà il
        // server a rifiutarla se serviva.
        console.warn('Token non leggibile dalle preferenze:', error);
    }

    return config;
});

axios.interceptors.response.use(
    async (response) => {
        // Il server rinnova la sessione quando sta per scadere e restituisce il
        // token nuovo in questa intestazione: va salvato, altrimenti l'utente
        // verrebbe disconnesso dopo un mese anche usando l'app ogni giorno.
        const rinnovato = response.headers?.['x-rinnovo-token'];
        if (rinnovato) {
            try {
                await Preferences.set({ key: 'token', value: rinnovato });
            } catch (errore) {
                console.warn('Token rinnovato non salvato:', errore);
            }
        }
        return response;
    },
    async (error) => {
        const status = error.response?.status;
        const url = error.config?.url;

        // 401 significa sessione non più valida: si esce e si torna al login.
        // Il 403 resta agli errori di permesso, che le pagine gestiscono da sé.
        if (status === 401 && isOurApi(url)) {
            try {
                await Preferences.remove({ key: 'token' });
                await Preferences.remove({ key: 'user' });
            } catch (removeError) {
                console.warn('Credenziali non rimosse:', removeError);
            }

            if (onUnauthorized) {
                onUnauthorized();
            } else {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export default axios;
